import React, { useState, useEffect, useMemo } from "react";
import { DayPicker, DateRange } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { PhotoDateRange } from "../utils/dateRangeUtils";

interface SearchDateRangePickerProps {
  value: { from?: Date; to?: Date };
  onChange: (range: { from?: Date; to?: Date }) => void;
  photoDateRange?: PhotoDateRange | null;
}

const MOBILE_BREAKPOINT = 768;
const MIN_VALID_YEAR = 1900;
const MAX_VALID_YEAR = 2100;
const DEFAULT_YEAR_RANGE = 20;

const isValidDateRange = (range: PhotoDateRange | null | undefined): range is PhotoDateRange => {
  if (!range?.minDate || !range?.maxDate) return false;

  const minYear = range.minDate.getFullYear();
  const maxYear = range.maxDate.getFullYear();

  return minYear >= MIN_VALID_YEAR &&
         maxYear <= MAX_VALID_YEAR &&
         minYear <= maxYear;
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const SearchDateRangePicker: React.FC<SearchDateRangePickerProps> = ({
  value,
  onChange,
  photoDateRange
}) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth <= MOBILE_BREAKPOINT
      : false
  );

  const [month, setMonth] = useState<Date>(() => value.from || new Date());

  useEffect(() => {
    if (!isValidDateRange(photoDateRange)) return;

    if (month < photoDateRange.minDate) {
      setMonth(photoDateRange.minDate);
    } else if (month > photoDateRange.maxDate) {
      setMonth(photoDateRange.maxDate);
    }
  }, [photoDateRange, month]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSelect = (range: DateRange | undefined) => {
    if (range) {
      onChange({ from: range.from, to: range.to });
    } else {
      onChange({ from: undefined, to: undefined });
    }
  };

  const years = useMemo(() => {
    if (isValidDateRange(photoDateRange)) {
      const minYear = photoDateRange.minDate.getFullYear();
      const maxYear = photoDateRange.maxDate.getFullYear();
      const yearCount = maxYear - minYear + 1;
      return Array.from({ length: yearCount }, (_, i) => minYear + i);
    }

    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: DEFAULT_YEAR_RANGE },
      (_, i) => currentYear - DEFAULT_YEAR_RANGE / 2 + i
    );
  }, [photoDateRange]);

  const handleYearChange = (year: number) => {
    const newMonth = new Date(month);
    newMonth.setFullYear(year);
    setMonth(newMonth);
  };

  const handleMonthChange = (monthIndex: number) => {
    const newMonth = new Date(month);
    newMonth.setMonth(monthIndex);
    setMonth(newMonth);
  };

  const goToPreviousMonth = () => {
    const newMonth = new Date(month);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setMonth(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(month);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setMonth(newMonth);
  };

  const disabledDates = useMemo(() => {
    if (!isValidDateRange(photoDateRange)) return undefined;

    return [
      { before: photoDateRange.minDate },
      { after: photoDateRange.maxDate }
    ];
  }, [photoDateRange]);

  return (
    <div className="search-date-range-picker">
      <div className="rdp-wrapper">
        <button
          type="button"
          className="rdp-nav_external"
          onClick={goToPreviousMonth}
          aria-label="Go to previous month"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>

        <DayPicker
          mode="range"
          selected={{ from: value.from, to: value.to }}
          onSelect={handleSelect}
          numberOfMonths={isMobile ? 1 : 2}
          month={month}
          onMonthChange={setMonth}
          showOutsideDays
          disableNavigation
          disabled={disabledDates}
          components={{
            Caption: ({ displayMonth }) => {
              const displayYear = displayMonth.getFullYear();
              const displayMonthIndex = displayMonth.getMonth();

              return (
                <div className="rdp-caption_custom">
                  <select
                    value={displayMonthIndex}
                    onChange={e => handleMonthChange(Number(e.target.value))}
                  >
                    {MONTHS.map((m, idx) => (
                      <option key={idx} value={idx}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    value={displayYear}
                    onChange={e => handleYearChange(Number(e.target.value))}
                  >
                    {years.map(y => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
          }}
        />

        <button
          type="button"
          className="rdp-nav_external"
          onClick={goToNextMonth}
          aria-label="Go to next month"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default SearchDateRangePicker;
